import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";

interface LocationState {
	codes?: string[];
}

export function MfaRecoveryCodesPage() {
	const location = useLocation();
	const navigate = useNavigate();
	const stateCodes = (location.state as LocationState | null)?.codes ?? [];
	const [codes] = useState<string[]>(stateCodes);
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (codes.length === 0) navigate("/profil/securite");
	}, [codes, navigate]);

	const copy = () => {
		navigator.clipboard.writeText(codes.join("\n")).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	const download = () => {
		const blob = new Blob([codes.join("\n")], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `akwaba-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
		a.click();
		URL.revokeObjectURL(url);
	};

	if (codes.length === 0) return null;

	return (
		<div>
			<div className="crumbs" style={{ marginBottom: 14, fontSize: 12 }}>
				<a
					className="root"
					style={{ cursor: "pointer" }}
					onClick={() => navigate("/profil/securite")}
				>
					Sécurité
				</a>
				<span className="sep">
					<AdminIcon n="chevR" s={10} />
				</span>
				<span className="here">Codes de récupération</span>
			</div>

			<Card style={{ maxWidth: 640 }}>
				<CardHead
					title={`${codes.length} codes de récupération générés`}
					sub="Conservez-les hors-ligne. Chaque code est à usage unique."
					right={
						<span className="tag success no-dot">
							<AdminIcon n="check" s={11} />
							MFA activée
						</span>
					}
				/>
				<CardBody>
					<div className="auth-alert warning">
						<span className="icon">
							<AdminIcon n="alert" s={16} />
						</span>
						<div>
							<strong>Ces codes ne seront affichés qu'une seule fois</strong>
							Si vous perdez votre application authenticator, ces codes seront
							le seul moyen de récupérer l'accès à votre compte.
						</div>
					</div>

					<div className="recovery-code-grid" data-testid="recovery-codes-grid">
						{codes.map((c) => (
							<div
								key={c}
								className="recovery-code"
								data-testid="recovery-code"
							>
								{c}
							</div>
						))}
					</div>

					<div
						style={{
							display: "flex",
							gap: 8,
							justifyContent: "flex-end",
							marginTop: 14,
						}}
					>
						<AdminButton
							variant="ghost"
							onClick={copy}
							data-testid="recovery-codes-copy"
						>
							<AdminIcon n="check" s={14} />
							{copied ? "Copié !" : "Copier"}
						</AdminButton>
						<AdminButton variant="ghost" onClick={download}>
							<AdminIcon n="download" s={14} />
							Télécharger
						</AdminButton>
						<AdminButton
							variant="primary"
							onClick={() => navigate("/profil/securite")}
							data-testid="recovery-codes-done"
						>
							J'ai sauvegardé mes codes
							<AdminIcon n="check" s={14} c="#fff" />
						</AdminButton>
					</div>
				</CardBody>
			</Card>
		</div>
	);
}
